import { supabase, isSupabaseConfigured, GALLERY_BUCKET } from './supabase';
import {
  buildFallbackCategory,
  buildFallbackGallery,
  buildFallbackSiteImages,
  fallbackSiteImage,
  DEFAULT_FIT,
  DEFAULT_FOCUS_X,
  DEFAULT_FOCUS_Y,
} from './fallback';

const GALLERY_CATEGORY = 'galleri';

const publicUrlFor = (path) =>
  supabase.storage.from(GALLERY_BUCKET).getPublicUrl(path).data.publicUrl;

// ---- Mappning DB-rad → frontend-form -------------------------------------

const mapImageRow = (row) => ({
  id: row.id,
  path: row.image_path,
  url: publicUrlFor(row.image_path),
  alt: row.title || '',
  title: row.title || '',
  category: row.category,
  project: row.project || '',
  isCover: !!row.is_cover,
  isTall: !!row.is_tall,
  rows: row.is_tall ? 2 : 1,
  fit: row.fit || DEFAULT_FIT,
  focusX: row.focus_x ?? DEFAULT_FOCUS_X,
  focusY: row.focus_y ?? DEFAULT_FOCUS_Y,
  sortOrder: row.sort_order ?? 0,
});

// ---- Uppladdning av en fil till Storage -----------------------------------

const uploadFile = async (file) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
  const { error } = await supabase.storage
    .from(GALLERY_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  return path;
};

// ==========================================================================
//  SITE-BILDER (enskilda "platser": hero, tjänsteomslag, porträtt)
// ==========================================================================

// Returnerar { slot: { url?, basePath?, filename?, alt, fit, focusX, focusY } }
// DB-rader vinner; saknade platser faller tillbaka på manifestet.
export const fetchSiteImages = async () => {
  const fallback = buildFallbackSiteImages();
  if (!isSupabaseConfigured) return fallback;
  try {
    const { data, error } = await supabase.from('site_images').select('*');
    if (error) throw error;
    const out = { ...fallback };
    for (const row of data || []) {
      out[row.slot] = {
        url: publicUrlFor(row.image_path),
        alt: fallback[row.slot]?.alt || '',
        fit: row.fit || DEFAULT_FIT,
        focusX: row.focus_x ?? DEFAULT_FOCUS_X,
        focusY: row.focus_y ?? DEFAULT_FOCUS_Y,
      };
    }
    return out;
  } catch (err) {
    console.error('Kunde inte hämta site-bilder:', err.message);
    return fallback;
  }
};

// Byt ut bilden för en plats (laddar upp ny fil + upsertar raden).
export const setSiteImage = async (slot, file, opts = {}) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const path = await uploadFile(file);
  const { error } = await supabase.from('site_images').upsert(
    {
      slot,
      image_path: path,
      fit: opts.fit || DEFAULT_FIT,
      focus_x: opts.focusX ?? DEFAULT_FOCUS_X,
      focus_y: opts.focusY ?? DEFAULT_FOCUS_Y,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'slot' }
  );
  if (error) throw error;
};

// Uppdatera bara passning/fokus för en plats.
export const updateSiteImageFit = async (slot, { fit, focusX, focusY }) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const { error } = await supabase
    .from('site_images')
    .update({ fit, focus_x: focusX, focus_y: focusY, updated_at: new Date().toISOString() })
    .eq('slot', slot);
  if (error) throw error;
};

export const siteImageMeta = (slot) => fallbackSiteImage(slot);

// ==========================================================================
//  PORTFOLIO (tjänstesidornas projekt) + GALLERI
// ==========================================================================

// Gruppera platta bildrader till projekt i stabil ordning.
const groupIntoProjects = (rows) => {
  const order = [];
  const byProject = new Map();
  for (const img of rows) {
    const key = img.project || '';
    if (!byProject.has(key)) {
      byProject.set(key, []);
      order.push(key);
    }
    byProject.get(key).push(img);
  }
  return order.map((title) => {
    const images = byProject.get(title);
    const cover = images.find((i) => i.isCover) || images[0];
    return { title, cover, images };
  });
};

// Tjänstesida: { title, description, sectionTitle, projects } – alltid med
// statiskt innehåll som fallback.
export const fetchCategory = async (category) => {
  const fallback = buildFallbackCategory(category);
  if (!isSupabaseConfigured) return fallback;
  try {
    const { data, error } = await supabase
      .from('portfolio_images')
      .select('*')
      .eq('category', category)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    if (!data || data.length === 0) return fallback;
    return { ...fallback, projects: groupIntoProjects(data.map(mapImageRow)) };
  } catch (err) {
    console.error(`Kunde inte hämta kategori ${category}:`, err.message);
    return fallback;
  }
};

// Platt lista av bildrader för en kategori (admin).
export const listCategoryImages = async (category) => {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('portfolio_images')
    .select('*')
    .eq('category', category)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapImageRow);
};

// Startsidans galleri (besökarvy) med fallback.
export const fetchGallery = async () => {
  const fallback = buildFallbackGallery();
  if (!isSupabaseConfigured) return fallback;
  try {
    const imgs = await listCategoryImages(GALLERY_CATEGORY);
    return imgs.length ? imgs : fallback;
  } catch (err) {
    console.error('Kunde inte hämta galleriet:', err.message);
    return fallback;
  }
};

export const listGalleryImages = () => listCategoryImages(GALLERY_CATEGORY);
export const GALLERY = GALLERY_CATEGORY;

// ---- CRUD för portfolio/galleri (kräver inloggning via RLS) ---------------

export const uploadPortfolioImage = async (file, opts = {}) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const path = await uploadFile(file);
  const { data, error } = await supabase
    .from('portfolio_images')
    .insert({
      category: opts.category,
      project: opts.project || '',
      title: opts.title || '',
      image_path: path,
      is_cover: !!opts.isCover,
      is_tall: !!opts.isTall,
      fit: opts.fit || DEFAULT_FIT,
      focus_x: opts.focusX ?? DEFAULT_FOCUS_X,
      focus_y: opts.focusY ?? DEFAULT_FOCUS_Y,
      sort_order: opts.sortOrder ?? Date.now(),
    })
    .select()
    .single();
  if (error) {
    await supabase.storage.from(GALLERY_BUCKET).remove([path]);
    throw error;
  }
  return mapImageRow(data);
};

export const updatePortfolioImage = async (id, patch) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const dbPatch = {};
  if (patch.title !== undefined) dbPatch.title = patch.title;
  if (patch.project !== undefined) dbPatch.project = patch.project;
  if (patch.isCover !== undefined) dbPatch.is_cover = patch.isCover;
  if (patch.isTall !== undefined) dbPatch.is_tall = patch.isTall;
  if (patch.fit !== undefined) dbPatch.fit = patch.fit;
  if (patch.focusX !== undefined) dbPatch.focus_x = patch.focusX;
  if (patch.focusY !== undefined) dbPatch.focus_y = patch.focusY;
  if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder;
  const { error } = await supabase.from('portfolio_images').update(dbPatch).eq('id', id);
  if (error) throw error;
};

// Sätt en bild som omslag för sitt projekt (nollställer övriga i projektet).
export const setProjectCover = async (image) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  await supabase
    .from('portfolio_images')
    .update({ is_cover: false })
    .eq('category', image.category)
    .eq('project', image.project);
  const { error } = await supabase.from('portfolio_images').update({ is_cover: true }).eq('id', image.id);
  if (error) throw error;
};

// Döp om ett projekt (uppdaterar alla bilder i projektet).
export const renameProject = async (category, oldName, newName) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const { error } = await supabase
    .from('portfolio_images')
    .update({ project: newName })
    .eq('category', category)
    .eq('project', oldName);
  if (error) throw error;
};

// Spara ny ordning för en lista bilder (t.ex. ett galleri eller ett projekt).
// Behåller listans "band" av sort_order-värden men lägger om positionerna.
export const persistImageOrder = async (orderedList) => {
  if (!isSupabaseConfigured || orderedList.length < 2) return;
  const base = Math.min(...orderedList.map((i) => i.sortOrder ?? 0));
  const updates = orderedList
    .map((img, idx) => ({ img, target: base + idx }))
    .filter(({ img, target }) => img.sortOrder !== target)
    .map(({ img, target }) => updatePortfolioImage(img.id, { sortOrder: target }));
  await Promise.all(updates);
};

export const deletePortfolioImage = async (image) => {
  if (!isSupabaseConfigured) throw new Error('Supabase är inte konfigurerat.');
  const { error } = await supabase.from('portfolio_images').delete().eq('id', image.id);
  if (error) throw error;
  if (image.path) await supabase.storage.from(GALLERY_BUCKET).remove([image.path]);
};
